import Vue from 'vue'
import Vuex from 'vuex'
import slug from 'slug'
import DataService from '../services/DataService'
import ConfigService, {
  contact as localContactConfig
} from '../services/ConfigService'
import loadStatus from '../utilities/loadStatus'
import logger from '../utilities/logger'
import {
  ACTION_PRIMARY,
  ACTION_ID_EMAIL,
  ACTION_ID_PHONE_PARTNER,
  ACTION_ID_LIVE_CHAT
} from '../utilities/topLevelActionsModel'
import TopLevelActionsFallback from '../utilities/TopLevelActionsFallback'
import articleActionsFromLabel from '../utilities/articleActionsFromLabel'
import cookies from './modules/cookies'
import snowplow from './modules/snowplow'
import supportRequestForm from './modules/supportRequestForm'
import reportConcernForm from './modules/reportConcernForm'
import content from '../../public/assets/content/copy.json'

Vue.use(Vuex)

const store = new Vuex.Store({
  modules: {
    cookies,
    snowplow,
    supportRequestForm,
    reportConcernForm
  },
  state: {
    articles: [],
    articleActions: [],
    content,
    featured: [],
    footerContent: {},
    isNativeAppRequest: false,
    loading: {},
    order: null,
    personalisation: {
      visible: true
    },
    topLevelActions: [],
    tenant: process.env.VUE_APP_TENANT,
    user: {
      email: null,
      id: null,
      isLoggedIn: false,
      name: null
    },
    customisationConfig: {
      liveChatEnabled: false,
      zopimUrl: ''
    },
    reportConcernPage: {}
  },
  mutations: {
    LOADING(state, status) {
      state.loading = { ...state.loading, ...status }
    },
    ARTICLES(state, data) {
      state.articles = data.Articles
      state.featured = data.Featured
        // Sort featured articles by position
        .slice()
        .sort((a, b) => a.Position - b.Position)
    },
    ARTICLE_ACTIONS(state, data) {
      state.articleActions = data
    },
    FOOTER_CONTENT(state, data) {
      state.footerContent = data
    },
    NATIVE_APP_REQUEST(state, data) {
      state.isNativeAppRequest = data
    },
    USER(state, data) {
      state.user = { ...state.user, ...data }
    },
    TOP_LEVEL_ACTIONS(state, data) {
      state.topLevelActions = [...data]
    },
    CUSTOMISATION_CONFIG(state, data) {
      state.customisationConfig = data
    },
    LOCALE(state, data) {
      state.locale = data
    },
    HIDE_PERSONALISATION(state) {
      state.personalisation.visible = false
    },
    ORDER(state, data) {
      state.order = data
    },
    REPORT_CONTENT_CONCERN(state, data) {
      state.reportConcernPage = data
    }
  },
  actions: {
    fetchLocale(context) {
      const html = document.querySelector('html')
      if (html && html.getAttribute('lang')) {
        context.commit('LOCALE', html.getAttribute('lang'))
      }
    },
    async fetchUser(context, auth) {
      if (auth && auth.userManager) {
        try {
          const user = await auth.userManager.getUser()
          if (user) {
            const { email, global_user_id: id, name } = user.profile
            const isLoggedIn = user.expired === false
            context.commit('USER', {
              email, id, isLoggedIn, name
            })
          }
        } catch (error) {
          logger.error('Error fetching user:', error)
        }
      }
    },
    async fetchArticles(context, auth) {
      const dataService = await DataService(auth)
      try {
        const data = await dataService.getArticles()
        context.commit('ARTICLES', data)
        context.commit('LOADING', { articles: loadStatus.complete })
      } catch (e) {
        context.commit('LOADING', {
          articles: loadStatus.fail,
          articlesError: e
        })
      }
    },
    async fetchReportConcern(context, { routeQueries, auth }) {
      const dataService = await DataService(auth)
      try {
        const res = await dataService.getContentConcernForm(routeQueries)
        context.commit('REPORT_CONTENT_CONCERN', res.data)
        context.commit('LOADING', { reportContentConcern: loadStatus.complete })
      } catch (e) {
        context.commit('LOADING', {
          reportContentConcern: loadStatus.fail,
          reportContentConcernError: e
        })
      }
    },
    async fetchFooterContent(context, auth) {
      const dataService = await DataService(auth)
      try {
        const data = await dataService.getFooterContent()
        context.commit('FOOTER_CONTENT', data)
        context.commit('LOADING', { footerContent: loadStatus.complete })
      } catch (e) {
        context.commit('LOADING', {
          footerContent: loadStatus.fail,
          footerContentError: e
        })
      }
    },
    async fetchArticleData(context, { articleId, auth }) {
      const { orderId } = context.getters
      const dataService = await DataService(auth)
      context.commit('ARTICLE_ACTIONS', [])
      try {
        const response = await dataService.getArticleData(articleId, orderId)
        const {
          config, order, topLevelActions, user
        } = response.data

        context.dispatch('snowplow/cx_articleById', articleId)
        if (config) {
          context.commit('CUSTOMISATION_CONFIG', config)
        }
        if (user) {
          context.commit('USER', user)
        }
        if (topLevelActions) {
          context.commit('ARTICLE_ACTIONS', topLevelActions)
        }
        if (order) {
          context.commit('ORDER', order)
          context.dispatch('snowplow/cx_order', order)
        }
      } catch (e) {
        const localCustomisationConfig =
            context.getters.localCustomisationConfig()
        context.commit('CUSTOMISATION_CONFIG', localCustomisationConfig)
        const articleLabelActions =
            context.getters.articleActionsFromLabel(articleId)
        context.commit('ARTICLE_ACTIONS', articleLabelActions)
        logger.error('Error loading article actions:', e)
      }
    },
    nativeAppRequest(context, isNative) {
      context.commit('NATIVE_APP_REQUEST', isNative)
    },
    setCustomisation(context, data) {
      const {
        config, order, topLevelActions, user
      } = data

      context.commit('CUSTOMISATION_CONFIG', config)
      context.commit('USER', user)
      context.commit('TOP_LEVEL_ACTIONS', topLevelActions)
      context.commit('ORDER', order)
      context.dispatch('snowplow/cx_order', order)
    },
    personalisationLoading(context) {
      context.commit('LOADING', {
        personalisation: loadStatus.active
      })
    },
    personalisationLoaded(context) {
      context.commit('LOADING', {
        personalisation: loadStatus.complete
      })
    },
    useFallbackForTopLevelActions(context) {
      const localCustomisationConfig =
        context.getters.localCustomisationConfig()
      context.commit('CUSTOMISATION_CONFIG', localCustomisationConfig)
      const data = TopLevelActionsFallback()
      context.commit('TOP_LEVEL_ACTIONS', data)
    },
    personalisationError(context, e) {
      context.commit('LOADING', { personalisation: loadStatus.fail })
      context.commit('HIDE_PERSONALISATION')
      logger.error(`Personalisation component error! Handing back control to fallback content. \n${e}`)
    },
    disableLiveChat(context, e) {
      if (e) {
        logger.error('Failed to load Live Chat:', e)
      }

      const lessChat = context.state.topLevelActions.filter(a => a.id !== ACTION_ID_LIVE_CHAT)
      if (lessChat.length > 0) {
        lessChat[0].buttonType = ACTION_PRIMARY
        context.commit('TOP_LEVEL_ACTIONS', lessChat)
      }
    }
  },
  getters: {
    articles(state) {
      return state.articles
    },
    categories(state) {
      const categories = []
      state.articles.forEach(article => {
        const newCategory =
          categories.find(cat => cat.id === article.SectionId) === undefined
        if (newCategory) {
          const category = {
            id: article.SectionId,
            position: article.SectionPosition,
            slug: slug(article.SectionName, { lower: true }),
            title: article.SectionName
          }
          categories.push(category)
        }
      })
      categories.sort((a, b) => a.position - b.position)
      return categories
    },
    findArticleById(state) {
      return id => state.articles.find(a => a.Id === parseInt(id, 10))
    },
    featuredArticles(state, getters) {
      const articles = []
      // 1. If no featured articles, return first 4 help articles
      if (state.featured.length === 0) {
        return state.articles.slice(0, 4)
      }

      // 2. Remove duplicates
      const uniqueIds = []
      state.featured.forEach(a => {
        if (!uniqueIds.includes(a.ArticleId)) {
          uniqueIds.push(a.ArticleId)
          const article = getters.findArticleById(a.ArticleId)
          if (article) {
            articles.push(article)
          }
        }
      })

      return articles.slice(0, 4)
    },
    articleActions(state) {
      return state.articleActions
    },
    articleActionsFromLabel(state, getters) {
      return id => {
        const article = getters.findArticleById(id)
        return articleActionsFromLabel(article)
      }
    },
    footerContent(state) {
      return state.footerContent
    },
    isNativeAppRequest(state) {
      return state.isNativeAppRequest
    },
    loadingState(state) {
      return state.loading
    },
    content(state) {
      return state.content
    },
    topLevelActions(state) {
      return state.topLevelActions
    },
    tenant(state) {
      return state.tenant
    },
    user(state) {
      return state.user
    },
    userInfoData(state) {
      if (state.user && state.user.isLoggedIn) {
        const user = {
          ...state.user,
          friendlyName: state.user.name,
          isAuthenticated: state.user.isLoggedIn
        }
        return user
      }
      return false
    },
    emailAction(state) {
      return state.topLevelActions.find(c => c.id === ACTION_ID_EMAIL) || null
    },
    emailArticleAction(state) {
      return state.articleActions.find(c => c.id === ACTION_ID_EMAIL) || null
    },
    phonePartnerArticleAction(state) {
      return (
        state.articleActions.find(c => c.id === ACTION_ID_PHONE_PARTNER) ||
        null
      )
    },
    phonePartnerAction(state) {
      return (
        state.topLevelActions.find(c => c.id === ACTION_ID_PHONE_PARTNER) ||
        null
      )
    },
    liveChatAction(state) {
      return (
        state.topLevelActions.find(a => a.id === ACTION_ID_LIVE_CHAT) || null
      )
    },
    articleLiveChatAction(state) {
      return (
        state.articleActions.find(a => a.id === ACTION_ID_LIVE_CHAT) || null
      )
    },
    liveChatDepartment(state, getters) {
      const liveChat = getters.liveChatAction

      if (liveChat) {
        return liveChat.department
      }

      return []
    },
    articleLiveChatDepartment(state, getters) {
      const liveChat = getters.articleLiveChatAction

      if (liveChat) {
        return liveChat.department
      }

      return []
    },
    liveChatTags(state, getters) {
      const liveChat = getters.liveChatAction
      let chatTags = []

      // liveChat.tags can be null
      if (liveChat && liveChat.tags) {
        chatTags = chatTags.concat(liveChat.tags)
      }

      return chatTags
    },
    articleLiveChatTags(state, getters) {
      const liveChat = getters.articleLiveChatAction
      let chatTags = []

      // liveChat.tags can be null
      if (liveChat && liveChat.tags) {
        chatTags = chatTags.concat(liveChat.tags)
      }

      return chatTags
    },
    liveChatMetadata(state, getters) {
      const liveChat = getters.liveChatAction

      // liveChat.tags can be null
      if (liveChat && liveChat.metadata) {
        return liveChat.metadata
      }

      return []
    },
    articleLiveChatMetadata(state, getters) {
      const liveChat = getters.articleLiveChatAction

      // liveChat.tags can be null
      if (liveChat && liveChat.metadata) {
        return liveChat.metadata
      }

      return []
    },
    personalisationEnabled(state) {
      return (
        ConfigService.personalisationEnabled() && state.personalisation.visible
      )
    },
    localCustomisationConfig() {
      const { enabled, url } = localContactConfig.liveChat
      const confObj = { liveChatEnabled: enabled, zopimUrl: url }
      return () => confObj
    },
    personalisationState(state) {
      return state.personalisation
    },
    categoryArticles(state) {
      return id => state.articles.filter(a => a.SectionId === parseInt(id, 10))
    },
    pageHeading(state) {
      return state.content.non_personalised_page_heading
    },
    appLocale(state) {
      return state.locale
    },
    contactEnabled(state) {
      return state.topLevelActions.length > 0 && state.user.isLoggedIn
    },
    customisationConfig(state) {
      return state.customisationConfig
    },
    reportConcernPage(state) {
      return state.reportConcernPage
    },
    orderId(state) {
      return state.order && state.order.id
    },
    reportConcernHeaderIconSrc(state) {
      const cssTheme = 'light'
      const { headerIcon } = state.reportConcernPage
      return headerIcon && headerIcon.url && headerIcon.url.replace(/{transformations}/, 'f_auto,w_120').replace(/{mode}/, cssTheme)
    },
    reportConcernHeaderIconName(state) {
      const { headerIcon } = state.reportConcernPage
      return headerIcon && headerIcon.name
    }
  }
})

export default store
